import { FC, useEffect, useRef } from 'react';
import { useTranslation } from '@frontend/i18n';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { Modal, useAlert } from '@frontend/design-system';
import { useSendFormsContext } from '../providers/context';
import { useSendFormsStore } from '../providers/store';
import { finishSendFormTracking, resetSendFormTracker, startSendFormTracker } from '../utils/tracker/tracker.utils';
import { modalStyle } from './send-forms-modal.styles';
import SendFormsSteps from './steps/send-forms-steps.component';

const SendFormsModal: FC = () => {
  const { t } = useTranslation('digital-forms-sending-experience');
  const { sendFormsModalControls, origin } = useSendFormsContext();
  const {
    currentStep,
    temporarilyHideModal,
    setTemporarilyHideModal,
    areFormsEnabledLocationIdsSet,
    formsEnabledLocationIds,
    clearStore,
  } = useSendFormsStore([
    'currentStep',
    'temporarilyHideModal',
    'setTemporarilyHideModal',
    'areFormsEnabledLocationIdsSet',
    'formsEnabledLocationIds',
    'clearStore',
  ]);
  const { isOpen: isSettingsOpen } = useSettingsNavigate();
  const alert = useAlert();
  const wasModalRestoredRef = useRef(false);
  const modalShoulBeRestoredRef = useRef(false);
  const {
    modalProps: { show: isModalOpen },
    openModal: openSendFormsModal,
    closeModal: closeSendFormsModal,
  } = sendFormsModalControls;

  /**
   * This is used to close the modal when the user navigates away to the settings page.
   * And resume the modal when the user navigates back from the settings page.
   */
  useEffect(() => {
    // If the modal is open and the settings page is open, close the modal temporarily.
    if (isSettingsOpen && isModalOpen) {
      modalShoulBeRestoredRef.current = true;
      closeSendFormsModal();
      setTemporarilyHideModal(true);
    }

    // If the modal was temporarily closed and the settings page is not open, reopen the modal.
    if (!isSettingsOpen && temporarilyHideModal) {
      // Timeout is used to ensure that the settings modal is closed before reopening this one.
      setTimeout(() => {
        setTemporarilyHideModal(false);
        modalShoulBeRestoredRef.current = false;
        wasModalRestoredRef.current = true;
        openSendFormsModal();
      }, 500);
    }
  }, [
    isSettingsOpen,
    temporarilyHideModal,
    isModalOpen,
    openSendFormsModal,
    closeSendFormsModal,
    setTemporarilyHideModal,
  ]);

  /**
   * This is used to show an alert when there are no locations enabled for sending forms.
   */
  useEffect(() => {
    if (!isModalOpen || !areFormsEnabledLocationIdsSet) {
      return;
    }

    if (formsEnabledLocationIds.length === 0) {
      alert.error(t('There are no locations enabled for sending forms.'));
      closeSendFormsModal();
    }
  }, [isModalOpen, formsEnabledLocationIds, areFormsEnabledLocationIdsSet, closeSendFormsModal, alert, t]);

  // Start tracking the modal open event
  useEffect(() => {
    if (wasModalRestoredRef.current) {
      wasModalRestoredRef.current = false;
      return;
    }

    if (isModalOpen) {
      startSendFormTracker();
    }
  }, [isModalOpen]);

  // Restore Send Forms tracker when modal is closed
  useEffect(() => {
    if (modalShoulBeRestoredRef.current) {
      return;
    }

    if (!isModalOpen) {
      resetSendFormTracker();
    }
  }, [isModalOpen]);

  function onCloseModalHandler() {
    finishSendFormTracking({
      isComplete: false,
      origin,
      step: currentStep,
    });
    closeSendFormsModal();
    clearStore(origin);
  }

  return (
    <Modal {...sendFormsModalControls.modalProps} css={modalStyle} onClose={onCloseModalHandler}>
      <SendFormsSteps />
    </Modal>
  );
};

export default SendFormsModal;
